import { Action, createReducer, on } from '@ngrx/store';
import { EntityAdapter, EntityState } from '@ngrx/entity/src/models';
import { createEntityAdapter } from '@ngrx/entity';

import * as segmentActions from '@live/actions/live-segments.actions';

import { guid } from '@common/modules/utils/string';

import ILiveSegment = Microsoft.VideoIndexer.Live.Contracts.ILiveSegment;
import ILiveSegmentsResults = Microsoft.VideoIndexer.Live.Contracts.ILiveSegmentsResults;

export interface IState extends EntityState<ILiveSegment> {
  loaded: boolean;
  isLoading: boolean;
  error: string | null;
  searchContext: ILiveSegmentsResults['searchContext'] | null;
  // no nextPage as the api is not returning paginated entities
}

function selectSegmentId(segment: ILiveSegment): string {
  return segment.id;
}

export const adapter: EntityAdapter<ILiveSegment> = createEntityAdapter<ILiveSegment>({
  selectId: selectSegmentId
});

export const initialState: IState = adapter.getInitialState({
  loaded: false,
  isLoading: false,
  error: null,
  searchContext: null
});

const liveSegmentsReducer = createReducer(
  initialState,
  on(segmentActions.searchStarted, state => ({
    ...state,
    searchContext: null,
    isLoading: true,
    loaded: false,
    error: null
  })),
  on(segmentActions.clearSearch, state => initialState),
  on(segmentActions.searchSucceeded, (state, action) => {
    const results: ILiveSegmentsResults = action.results;
    const { results: segments, searchContext } = results;
    // we must create our own computed id since backend not returning an id that we can rely on for mapping and accessing the entities
    const newSegments = segments.map(segment => ({ ...segment, id: guid() })) || [];
    // We use set all instead of upsert because we want to always clear and use the latest fresh search state, never keep old search results
    return adapter.setAll(newSegments, {
      ...state,
      searchContext,
      isLoading: false,
      loaded: true,
      error: null
    });
  }),
  on(segmentActions.searchFailed, (state, { error }) => ({
    ...state,
    searchContext: null,
    isLoading: false,
    loaded: true,
    error: error,
    entities: {},
    ids: []
  }))
);

export function reducer(state: IState | undefined, action: Action) {
  return liveSegmentsReducer(state, action);
}
